import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, Shield, Heart, AlertTriangle, Lock, Send, Facebook } from "lucide-react";
import { subscribeNewsletter } from "@/lib/api";
import { toast } from "sonner";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!consent) {
      toast.error("Add meg a hozzájárulást az adatvédelmi tájékoztatóhoz.");
      return;
    }
    setLoading(true);
    try {
      await subscribeNewsletter(email, "footer");
      toast.success("Sikeresen feliratkoztál! Köszönjük.");
      setEmail("");
      setConsent(false);
    } catch (err) {
      toast.error("Hiba történt. Próbáld újra.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="mt-20 border-t border-white/10 bg-card/40" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 lg:px-6 py-12 grid grid-cols-1 md:grid-cols-4 gap-10">
        {/* Newsletter */}
        <div className="md:col-span-2">
          <div className="flex items-center gap-2 mb-2">
            <img src="https://customer-assets.emergentagent.com/job_budget-1/artifacts/yg5l14zp_file_00000000e9c871f4af8f72089f9268c0.png" alt="Bonuszvadász" className="w-10 h-10 rounded-lg object-cover" />
            <span className="font-display font-black text-2xl">Bonusz<span className="text-gold">vadász</span></span>
          </div>
          <p className="text-white/70 text-sm mb-4 max-w-md">
            🎯 <strong>Bónusz Riasztó.</strong> Iratkozz fel hírlevelünkre, és elsőként értesülj a legjobb új bónuszokról, exkluzív promóciós kódokról.
          </p>
          <form onSubmit={submit} className="space-y-2 max-w-md" data-testid="newsletter-form">
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="email@pelda.hu"
                className="flex-1 bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-white outline-none focus:ring-2 focus:ring-gold/50"
                data-testid="newsletter-email"
              />
              <button
                type="submit"
                disabled={loading}
                className="gold-btn rounded-lg px-5 py-2.5 disabled:opacity-50"
                data-testid="newsletter-submit"
              >
                {loading ? "..." : "Feliratkozom"}
              </button>
            </div>
            <label className="flex items-start gap-2 text-xs text-white/60">
              <input
                type="checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                className="mt-0.5"
                data-testid="newsletter-consent"
              />
              <span>Elfogadom az <Link to="/adatvedelem" className="text-gold underline">adatvédelmi tájékoztatót</Link> és kérek időszakos email értesítéseket.</span>
            </label>
          </form>

          {/* Social links */}
          <div className="mt-5">
            <h4 className="text-sm font-bold text-white/90 mb-2">Kövess minket</h4>
            <div className="flex flex-wrap items-center gap-3" data-testid="footer-socials">
              <a
                href="https://t.me/sporttippekbonuszok"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Telegram csatorna"
                title="Telegram: Tippek & Kaszinó Bónusz"
                data-testid="footer-telegram-link"
                className="group flex items-center gap-2 bg-white/5 hover:bg-gold/15 border border-white/10 hover:border-gold/50 rounded-full pl-3 pr-4 py-2 transition-all"
              >
                <span className="w-7 h-7 rounded-full bg-[#229ED9] flex items-center justify-center text-white">
                  <Send size={14} />
                </span>
                <span className="text-sm text-white/80 group-hover:text-gold font-medium">Telegram</span>
              </a>
              <a
                href="https://www.facebook.com/share/1EG9927z1c/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook oldal"
                title="Facebook: Bonuszvadász"
                data-testid="footer-facebook-link"
                className="group flex items-center gap-2 bg-white/5 hover:bg-gold/15 border border-white/10 hover:border-gold/50 rounded-full pl-3 pr-4 py-2 transition-all"
              >
                <span className="w-7 h-7 rounded-full bg-[#1877F2] flex items-center justify-center text-white">
                  <Facebook size={14} />
                </span>
                <span className="text-sm text-white/80 group-hover:text-gold font-medium">Facebook</span>
              </a>
              <a
                href="https://www.facebook.com/share/g/1DPtRC1h9f/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook csoport - Bonuszvadász közösség"
                title="Csatlakozz a Bonuszvadász Facebook csoporthoz"
                data-testid="footer-fbgroup-link"
                className="group flex items-center gap-2 bg-[#1877F2]/15 hover:bg-[#1877F2]/30 border border-[#1877F2]/40 rounded-full pl-3 pr-4 py-2 transition-all"
              >
                <span className="w-7 h-7 rounded-full bg-[#1877F2] flex items-center justify-center text-white">
                  <Facebook size={14} />
                </span>
                <span className="text-sm text-white group-hover:text-white font-bold">FB Csoport</span>
              </a>
            </div>
          </div>
        </div>

        <div>
          <h3 className="font-display font-bold text-white mb-3">Felfedezés</h3>
          <ul className="space-y-2 text-sm">
            <li><Link to="/kaszinok" className="text-white/70 hover:text-gold">Online kaszinók</Link></li>
            <li><Link to="/jatekok" className="text-white/70 hover:text-gold" data-testid="footer-games-link">Kaszinó játékok</Link></li>
            <li><Link to="/sportfogadas" className="text-white/70 hover:text-gold">Sportfogadás</Link></li>
            <li><Link to="/bonuszok" className="text-white/70 hover:text-gold">Bónuszok</Link></li>
            <li><Link to="/kalkulatorok" className="text-white/70 hover:text-gold">Kalkulátorok</Link></li>
            <li><Link to="/strategiak" className="text-white/70 hover:text-gold">Stratégiák</Link></li>
            <li><Link to="/akademia" className="text-white/70 hover:text-gold">Akadémia</Link></li>
            <li><Link to="/szakszotar" className="text-white/70 hover:text-gold">Szakszótár</Link></li>
            <li><Link to="/blog" className="text-white/70 hover:text-gold">Blog</Link></li>
          </ul>
        </div>

        <div>
          <h3 className="font-display font-bold text-white mb-3">A Bonuszvadász</h3>
          <ul className="space-y-2 text-sm">
            <li><Link to="/rolunk" className="text-white/70 hover:text-gold">Rólunk</Link></li>
            <li><Link to="/kapcsolat" className="text-white/70 hover:text-gold">Kapcsolat / Panasz</Link></li>
            <li><Link to="/felelos-jatek" className="text-white/70 hover:text-gold">Felelős játék</Link></li>
            <li><Link to="/biztonsag-licencek" className="text-white/70 hover:text-gold">Biztonság & Licencek</Link></li>
            <li><Link to="/adatvedelem" className="text-white/70 hover:text-gold">Adatvédelem</Link></li>
            <li><Link to="/aszf" className="text-white/70 hover:text-gold">ÁSZF</Link></li>
            <li><Link to="/cookie-iranyelvek" className="text-white/70 hover:text-gold">Cookie irányelvek</Link></li>
            <li><Link to="/affiliate-kozlemeny" className="text-white/70 hover:text-gold">Affiliate közlemény</Link></li>
            <li><a href="mailto:hello@bonuszvadasz.com" className="text-gold hover:underline flex items-center gap-1" data-testid="footer-email"><Mail size={12} />hello@bonuszvadasz.com</a></li>
          </ul>
        </div>
      </div>

      {/* Responsible Gambling Trust Badges */}
      <div className="border-t border-white/10 bg-card/30">
        <div className="max-w-7xl mx-auto px-4 lg:px-6 py-6">
          <div className="text-center mb-4">
            <h4 className="text-xs uppercase tracking-widest text-white/50 font-bold mb-1">Felelős játék — Trusted partners</h4>
            <p className="text-[11px] text-white/40">Ha úgy érzed, hogy a játék kontrollálhatatlan, fordulj az alábbi szervezetekhez segítségért.</p>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-3 md:gap-4" data-testid="responsible-gambling-badges">
            <a
              href="https://www.begambleaware.org"
              target="_blank"
              rel="noopener noreferrer nofollow"
              title="BeGambleAware — nemzetközi felelős játék szervezet"
              data-testid="badge-begambleaware"
              className="flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/30 rounded-lg px-3 py-2 transition-all"
            >
              <span className="w-7 h-7 rounded-full bg-gradient-to-br from-orange-400 to-rose-500 flex items-center justify-center text-white text-[10px] font-black">BGA</span>
              <span className="text-xs text-white/80 font-medium">BeGambleAware<span className="text-white/40">.org</span></span>
            </a>
            <a
              href="https://www.gamcare.org.uk"
              target="_blank"
              rel="noopener noreferrer nofollow"
              title="GamCare — szerencsejáték függőség kezelés"
              data-testid="badge-gamcare"
              className="flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/30 rounded-lg px-3 py-2 transition-all"
            >
              <span className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center text-white text-[10px] font-black">GC</span>
              <span className="text-xs text-white/80 font-medium">GamCare</span>
            </a>
            <a
              href="https://www.gamblingtherapy.org"
              target="_blank"
              rel="noopener noreferrer nofollow"
              title="Gambling Therapy — globális online segítség"
              data-testid="badge-gambling-therapy"
              className="flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/30 rounded-lg px-3 py-2 transition-all"
            >
              <span className="w-7 h-7 rounded-full bg-gradient-to-br from-emerald-500 to-teal-400 flex items-center justify-center text-white text-[10px] font-black">GT</span>
              <span className="text-xs text-white/80 font-medium">Gambling Therapy</span>
            </a>
            <a
              href="https://www.gamstop.co.uk"
              target="_blank"
              rel="noopener noreferrer nofollow"
              title="GAMSTOP — önkizárás szerencsejátékból"
              data-testid="badge-gamstop"
              className="flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/30 rounded-lg px-3 py-2 transition-all"
            >
              <span className="w-7 h-7 rounded-full bg-gradient-to-br from-red-500 to-pink-500 flex items-center justify-center text-white text-[10px] font-black">GS</span>
              <span className="text-xs text-white/80 font-medium">GAMSTOP</span>
            </a>
            <a
              href="https://jatszma.org"
              target="_blank"
              rel="noopener noreferrer nofollow"
              title="Játszma.org — magyar országos felelős játék program"
              data-testid="badge-jatszma"
              className="flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/30 rounded-lg px-3 py-2 transition-all"
            >
              <span className="w-7 h-7 rounded-full bg-gradient-to-br from-gold to-amber-500 flex items-center justify-center text-background text-[10px] font-black">JM</span>
              <span className="text-xs text-white/80 font-medium">Játszma<span className="text-white/40">.org</span></span>
            </a>
            <a
              href="https://www.gamblersanonymous.org"
              target="_blank"
              rel="noopener noreferrer nofollow"
              title="Gamblers Anonymous — anonim játékosok"
              data-testid="badge-ga"
              className="flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/30 rounded-lg px-3 py-2 transition-all"
            >
              <span className="w-7 h-7 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center text-white text-[10px] font-black">GA</span>
              <span className="text-xs text-white/80 font-medium">Gamblers Anonymous</span>
            </a>
            <div
              title="18+ — Csak 18 év felettieknek"
              className="flex items-center gap-2 bg-rose-500/15 border border-rose-500/40 rounded-lg px-3 py-2"
              data-testid="badge-18plus"
            >
              <span className="w-7 h-7 rounded-full bg-rose-500 flex items-center justify-center text-white text-[11px] font-black">18+</span>
              <span className="text-xs text-rose-200 font-medium">Csak 18 felett</span>
            </div>
          </div>
        </div>
      </div>

      {/* Guest post / backlink együttműködés */}
      <div className="border-t border-white/10 bg-card/20">
        <div className="max-w-7xl mx-auto px-4 lg:px-6 py-5 text-center" data-testid="footer-guest-post">
          <div className="text-[10px] uppercase tracking-widest text-gold font-bold mb-1">Vendégcikk & Backlink együttműködés</div>
          <p className="text-xs text-white/70">
            Ha partneri linkcserét vagy vendégposztot szeretnél, írj nekünk:{" "}
            <a href="mailto:hello@bonuszvadasz.com" className="text-gold hover:underline font-medium" data-testid="footer-guest-email">hello@bonuszvadasz.com</a>
          </p>
        </div>
      </div>

      {/* Trust + 18+ */}
      <div className="border-t border-white/10 bg-background/60">
        <div className="max-w-7xl mx-auto px-4 lg:px-6 py-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3 text-xs text-white/50">
            <span className="bg-neon/20 border border-neon/40 text-neon rounded-full px-2 py-0.5 font-bold">18+</span>
            <span className="hidden md:inline">|</span>
            <span className="flex items-center gap-1"><AlertTriangle size={12} /> A szerencsejáték függőséget okozhat. Játssz felelősen.</span>
            <span className="hidden md:inline">|</span>
            <Link to="/felelos-jatek" className="text-gold hover:underline flex items-center gap-1"><Heart size={12} />Segítség: Felelős játék</Link>
          </div>
          <div className="flex items-center gap-3 text-xs text-white/50">
            <span className="flex items-center gap-1"><Shield size={12} />SSL védett</span>
            <span>© {new Date().getFullYear()} Bonuszvadász</span>
            <span className="hidden md:inline">|</span>
            <Link
              to="/admin"
              className="flex items-center gap-1 text-white/40 hover:text-gold transition-colors"
              data-testid="footer-admin-link"
              title="Admin belépés"
            >
              <Lock size={12} /> Admin
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
